import {
    Component,
    OnInit,
    Inject,
    OnChanges,
    SimpleChanges,
    DestroyRef,
    inject
} from '@angular/core';
import {FormGroup, FormBuilder} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, of, throwError} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, take, tap} from 'rxjs/operators';
import {TreeNode} from '@ali-hm/angular-tree-component';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {MenuService} from '@emm/core/services/menu.service';
import {MenuStoreService} from '@emm/menu/services/menu-store.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {getCascadeMatchPath, CascadeChip, MatchPathResult} from '@emm/core/util/cascade-matchpath.util';

interface ChipState {
    name: string;
    selected: boolean;
    tag: string;
}

@Component({
    selector: 'emm-paste-child-item-dialog',
    styleUrls: ['./paste-child-item-dialog.component.scss'],
    templateUrl: './paste-child-item-dialog.component.html',
    standalone: false
})
export class PasteChildItemDialogComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    color = '#e5173f';
    diameter = 25;
    featureScope: string;
    form: FormGroup;
    firstFormGroup: FormGroup;
    isLinear = true;
    menuKey: MenuKeyContext;
    mode = 'indeterminate';
    options: LocationSet[];
    parent: TreeNode;
    copiedItem = this.data.copiedItem;
    response;
    searched: boolean;
    secondFormGroup: FormGroup;
    subMenuTag;
    currentSubmenu;
    cascadeSubmenus$ = new BehaviorSubject([]);
    submenusCache = [];
    selectedSubmenus: string[] = [];
    item;
    attributes = [];
    attributes$ = new BehaviorSubject([]);
    searchForm = this.formBuilder.group({searchTerm: ['']});

    // Reactive state management
    bottomChips$ = new BehaviorSubject<ChipState[]>([]);
    matchCount$ = new BehaviorSubject<number>(0);
    descendantCount$ = new BehaviorSubject<number>(0);
    spinner$ = new BehaviorSubject<boolean>(false);
    path$ = new BehaviorSubject<object | string | null>(null);
    showPath$ = new BehaviorSubject<boolean>(false);

    filterSize: number;
    topSelected = new BehaviorSubject<boolean>(false);
    bottomSelected = new BehaviorSubject<boolean>(false);
    useNewCascadeMatchPaths$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_NEW_CASCADE_MATCH_PATHS);
    useNewCascadeMatchPaths = false;

    // Computed observables
    selectedBottomChips$ = this.bottomChips$.pipe(
        map(chips => chips.filter(chip => chip.selected))
    );

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<PasteChildItemDialogComponent>,
        private menuService: MenuService,
        private menuStore: MenuStoreService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
        private launchDarklyService: LaunchDarklyService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
    }

    ngOnInit(): void {
        this.featureScope = this.data.featureScope;
        this.parent = this.data.parent;
        this.menuKey = this.data.menuKey;
        this.treeFacade.resetCascadeSubmenus();
        this.listenForCascadeChange();

        this.useNewCascadeMatchPaths$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.useNewCascadeMatchPaths = value);

        this.api.getSubmenusByFeatureScope(this.featureScope, this.menuKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    this.cascadeSubmenus$.next(result);
                    this.submenusCache = result;
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });

        this.firstFormGroup = this.formBuilder.group({});
        this.secondFormGroup = this.formBuilder.group({});

        this.loadTargetedItem().then(() => {
            this.menuStore.queryItemDetail.selected$
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(item => {
                    this.item = item;
                    this.buildAttributes();
                    setTimeout(() => {
                        this.findCurrentSubmenu();
                        this.initChips();
                        this.recalculatePath();
                    }, 1);
                });
        });

        // React to chip selection changes and recalculate path
        this.bottomChips$
            .pipe(
                debounceTime(50),
                distinctUntilChanged((prev, curr) =>
                    JSON.stringify(prev) === JSON.stringify(curr)
                ),
                tap(() => this.recalculatePath()),
                switchMap(() => this.findMatches(this.featureScope, this.path$.value)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }


    ngOnChanges(changes: SimpleChanges) {
        this.refreshCascadeSubmenus();
    }

    async loadTargetedItem() {
        await this.menuStore.dispatch.loadItemDetail(this.copiedItem);
    }

    refreshCascadeSubmenus() {
        this.treeFacade.selectedSubmenusPaste$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                this.selectedSubmenus = [...result].filter(submenu => submenu !== null && submenu !== undefined);
            });
    }

    deselectCascadeSubmenus(submenuTag: string) {
        if (this.selectedSubmenus.includes(submenuTag)) {
            this.selectedSubmenus.splice(this.selectedSubmenus.indexOf((submenuTag)));
        }
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        this.selectedBottomChips$.pipe(take(1)).subscribe(selectedChips => {
            const result = {
                item: this.copiedItem.tag,
                parentPath: this.getPasteCascadePath(selectedChips),
                submenuTag: this.subMenuTag,
                position: this.topSelected.value ? 'top' : 'bottom',
            };
            this.dialogRef.close(result);
        });
    }

    togglePath(): void {
        this.showPath$.next(!this.showPath$.value);
    }

    createChip({name = '', tag = ''} = {}, selected: boolean): ChipState {
        return {name, selected, tag};
    }

    initChips(): void {
        // find item hierarchy
        const nodes: ChipState[] = [];
        nodes.push(this.createChip(this.parent.data.data, true));
        this.findParents(this.parent).forEach((parent) =>
            nodes.push(this.createChip(parent.data.data, false))
        );
        const chips = nodes.reverse();
        const bottomChips = chips.slice(1);
        if (bottomChips.length === 1) {
            bottomChips[0].selected = true;
        }
        this.bottomChips$.next(bottomChips);

        // Initial path calculation
        this.recalculatePath();
    }

    toggleSelectBottomChip(chip: ChipState): void {
        const currentChips = this.bottomChips$.value;
        const chipIndex = currentChips.findIndex(c => c.tag === chip.tag);

        if (chipIndex === -1) return;

        // Toggle selected chip
        const newValue = !chip.selected;
        const updatedChips = [...currentChips];
        updatedChips[chipIndex] = {...updatedChips[chipIndex], selected: newValue};

        // If selecting, set all child descendants to true
        if (newValue) {
            for (let i = chipIndex + 1; i < updatedChips.length; i++) {
                updatedChips[i] = {...updatedChips[i], selected: true};
            }
        }

        // Validate deselecting intermediate parents
        const selectedIndexes = updatedChips
            .map((ch, index) => (ch.selected ? index : -1))
            .filter(index => index !== -1);

        if (selectedIndexes.length > 1) {
            const sortedIndexes = selectedIndexes.sort((a, b) => a - b);
            const gapCount = sortedIndexes.reduce((count, index, i) => {
                if (i < sortedIndexes.length - 1 && sortedIndexes[i + 1] - index > 1) {
                    return count + 1;
                }
                return count;
            }, 0);

            if (gapCount > 0) {
                this.notificationService.error('Cannot deselect intermediate parent');
                return;
            }
        }

        // Update state immutably
        this.bottomChips$.next(updatedChips);
    }

    private recalculatePath(): void {
        const chips = this.bottomChips$.value;
        const selectedBottomChips = chips.filter(chip => chip.selected);
        const path = this.getPath(selectedBottomChips);
        this.path$.next(path);
    }

    getPath(selectedBottomChips: ChipState[]): MatchPathResult {
        return getCascadeMatchPath(
            selectedBottomChips as CascadeChip[],
            this.bottomChips$.value as CascadeChip[],
            this.useNewCascadeMatchPaths
        );
    }

    getPasteCascadePath(selectedBottomChips: ChipState[]): string {
        let parentMatchPath = '';
        for (const chip of selectedBottomChips) {
            parentMatchPath += `/${chip.tag}`;
        }
        return parentMatchPath;
    }

    getDescendantCount() {
        return this.selectedBottomChips$.pipe(
            take(1),
            switchMap(selectedChips => {
                const path = this.getPasteCascadePath(selectedChips);
                return this.menuService.countDescendantsForPasteCascade(path, this.copiedItem, this.selectedSubmenus);
            })
        );
    }

    findMatches(featureScope: string, path: any): any {
        this.spinner$.next(true);

        this.getDescendantCount()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result: number) => {
                    this.descendantCount$.next(result);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage)
                }
            });

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesForUpdateCascade(featureScope, this.data.effectiveDate, this.menuKey, path, this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.spinner$.next(false);
                        this.matchCount$.next(results);
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching matches';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                        return throwError(errorMessage);
                    })
                );

            return source$;
        }

        this.spinner$.next(false);
        return of(null);
    }

    updateAllSelected(checked: boolean, checkedValue: string): void {
        if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
            this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
        }

        this.spinner$.next(true);
        if (checked) {
            this.treeFacade.selectCascadeSubmenus(checkedValue);
            this.refreshCascadeSubmenus();
        } else {
            this.treeFacade.deselectCascadeSubmenus(checkedValue);
            this.deselectCascadeSubmenus(checkedValue);
        }

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService.countMatchesForUpdateCascade(
                this.featureScope,
                this.data.effectiveDate,
                this.menuKey,
                this.path$.value,
                this.selectedSubmenus
            )
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (results) => {
                        this.matchCount$.next(results);
                        this.spinner$.next(false);
                    },
                    error: () => {
                        const errorMessage = 'Error searching parent nodes';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                    }
                });
        }
    }

    findParents(item: TreeNode): TreeNode[] {
        return this.findParent(item, []);
    }

    findParent(node: TreeNode, parents: TreeNode[]): TreeNode[] {
        const parent = node.parent;
        if (node && !node.isRoot) {
            parents.push(parent);
            return this.findParent(parent, parents);
        }
        return parents;
    }

    findCurrentSubmenu() {
        try {
            this.currentSubmenu = this.parent.data.data.tagPath.split('/').filter(parent => parent !== '')[0];
            if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
                this.selectedSubmenus.push(this.currentSubmenu);
                this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
            }
        } catch (e) {
        }
    }

    buildAttributes(): void {
        try {
            const tagAttribute = {
                attributeGroup: 'CORE',
                dateUpdated: new Date().toISOString(),
                tag: 'TAG',
                name: 'Tag',
                jsonProperty: 'tag',
                description: 'Unique item identifier',
                options: null,
                type: 'TEXT',
                required: true,
                scope: 'GLOBAL',
                label: 'Tag',
                disabled: true,
                value: this.item.tag
            };
            this.attributes.push(tagAttribute);
            const typeAttribute = {
                attributeGroup: 'CORE',
                dateUpdated: new Date().toISOString(),
                tag: 'TYPE',
                name: 'Type',
                jsonProperty: 'type',
                description: 'Item type',
                type: 'TEXT',
                required: true,
                scope: 'GLOBAL',
                label: 'Type',
                disabled: true,
                value: this.item.typeName
            };
            this.attributes.push(typeAttribute);
            this.copyItemAttributes();
            this.orderAttributes();
        } catch (e) {
        }
    }

    copyItemAttributes() {
        this.item.attributes.forEach(attribute => this.attributes.push(attribute));
    }

    orderAttributes() {
        let orderedAttributes = [];
        for (const attribute of this.attributes) {
            switch (attribute.tag) {
                case 'NAME':
                    orderedAttributes[0] = attribute;
                    break;
                case 'TAG':
                    orderedAttributes[1] = attribute;
                    break;
                case 'TYPE':
                    orderedAttributes[2] = attribute;
                    break;
            }
        }
        const remainingAttributes = this.item.attributes.filter(attribute => this.attributes.includes(attribute));
        orderedAttributes = orderedAttributes.concat(remainingAttributes);
        this.attributes$.next(orderedAttributes);
    }

    onCascadeFilter($event: Event): void {
        const filterStr = ($event.target as HTMLInputElement).value;
        setTimeout(() => {
            this.filterCascadeSubmenus(filterStr);
        }, 725);
    }

    filterCascadeSubmenus(value: string): void {
        if (value === '') {
            this.cascadeSubmenus$.next(this.submenusCache);
        } else {
            this.cascadeSubmenus$.pipe(
                take(1),
                map((list: any) => {
                    const filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()) ||
                        nd['id'].toLowerCase().includes(value.toLowerCase()));
                    this.cascadeSubmenus$.next(filteredNodes);
                }),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe();
        }
    }

    listenForCascadeChange(): void {
        this.cascadeSubmenus$.asObservable()
            .pipe(
                map((values) => values ? this.filterSize = values.length : this.filterSize = 0),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    resetFilter(): void {
        this.searchForm.controls['searchTerm'].setValue('');
        this.cascadeSubmenus$.next(this.submenusCache);
    }

    setTopSelected() {
        this.topSelected.next(!!this.topSelected);
        this.bottomSelected.next(false);
    }

    setBottomSelected() {
        this.bottomSelected.next(!!this.bottomSelected);
        this.topSelected.next(false);
    }
}
